/**
 * Type-safe HTTP client for the Melusine Shop DRF API.
 *
 * Every request is authenticated with the raw Telegram ``initData`` carried in the
 * ``Authorization: tma <…>`` header (validated server-side by HMAC). The client is
 * intentionally thin — JSON in/out, error normalization — and is meant to be paired
 * with the OpenAPI-generated types in {@link ./generated/schema} for end-to-end type
 * safety at the call sites (and with the query hooks in {@link ./hooks}).
 *
 * The frontend is *untrusted*: prices, totals, limits and balances are always
 * recomputed by the server. This client never derives economic values locally.
 */
import { getAuthHeaderValue } from "@melusine/shared-telegram";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiClientConfig {
  /** API base URL, e.g. ``/api`` (default) or ``https://host/api``. */
  readonly baseUrl?: string;
  /**
   * Resolver for the ``Authorization`` header value (``tma <raw initData>``), or
   * ``null`` when unavailable. Defaults to the Telegram SDK resolver. Override in
   * tests or non-Telegram contexts.
   */
  readonly getAuthHeader?: () => string | null;
  /** Extra default headers merged into every request. */
  readonly defaultHeaders?: Readonly<Record<string, string>>;
}

export interface RequestOptions<TBody> {
  /** Query parameters; ``undefined``/``null`` values are dropped. */
  readonly query?: Readonly<
    Record<string, string | number | boolean | null | undefined>
  >;
  /** JSON request body (mutually exclusive with ``formData``). */
  readonly body?: TBody;
  /** Multipart body for file uploads (e.g. SBP proof). Bypasses JSON encoding. */
  readonly formData?: FormData;
  /** Idempotency key forwarded as ``Idempotency-Key`` (order/currency ops). */
  readonly idempotencyKey?: string;
  /** Abort signal for cancellation. */
  readonly signal?: AbortSignal;
}

/** Normalized API error carrying the HTTP status and the parsed DRF payload. */
export class ApiError extends Error {
  public override readonly name = "ApiError";

  public constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly payload: unknown,
  ) {
    super(`API ${status} ${statusText}`);
  }

  /** ``true`` for 401/403 — re-validate initData / role gate. */
  public get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** ``true`` for 409 — idempotency conflict or invalid FSM transition. */
  public get isConflict(): boolean {
    return this.status === 409;
  }
}

function buildQueryString(
  query: RequestOptions<unknown>["query"],
): string {
  if (!query) {
    return "";
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return null;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as unknown;
  }
  return await response.text();
}

/** A configured, type-safe API client. Create once and share across the app. */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly getAuthHeader: () => string | null;
  private readonly defaultHeaders: Readonly<Record<string, string>>;

  public constructor(config: ApiClientConfig = {}) {
    this.baseUrl = (config.baseUrl ?? "/api").replace(/\/$/, "");
    this.getAuthHeader = config.getAuthHeader ?? getAuthHeaderValue;
    this.defaultHeaders = config.defaultHeaders ?? {};
  }

  /**
   * Perform a request and return the parsed JSON body typed as ``TResponse``.
   *
   * @throws {@link ApiError} on any non-2xx response.
   */
  public async request<TResponse, TBody = unknown>(
    method: HttpMethod,
    path: string,
    options: RequestOptions<TBody> = {},
  ): Promise<TResponse> {
    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}${buildQueryString(options.query)}`;

    const headers: Record<string, string> = {
      Accept: "application/json",
      ...this.defaultHeaders,
    };

    const auth = this.getAuthHeader();
    if (auth !== null) {
      headers["Authorization"] = auth;
    }
    if (options.idempotencyKey !== undefined) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }

    let payload: BodyInit | undefined;
    if (options.formData !== undefined) {
      payload = options.formData; // browser sets multipart boundary
    } else if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(options.body);
    }

    const response = await fetch(url, {
      method,
      headers,
      ...(payload !== undefined ? { body: payload } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

    const parsed = await parseResponse(response);
    if (!response.ok) {
      throw new ApiError(response.status, response.statusText, parsed);
    }
    return parsed as TResponse;
  }

  public get<TResponse>(
    path: string,
    options?: Omit<RequestOptions<never>, "body" | "formData">,
  ): Promise<TResponse> {
    return this.request<TResponse>("GET", path, options);
  }

  public post<TResponse, TBody = unknown>(
    path: string,
    options?: RequestOptions<TBody>,
  ): Promise<TResponse> {
    return this.request<TResponse, TBody>("POST", path, options);
  }

  public patch<TResponse, TBody = unknown>(
    path: string,
    options?: RequestOptions<TBody>,
  ): Promise<TResponse> {
    return this.request<TResponse, TBody>("PATCH", path, options);
  }

  public delete<TResponse>(
    path: string,
    options?: Omit<RequestOptions<never>, "body" | "formData">,
  ): Promise<TResponse> {
    return this.request<TResponse>("DELETE", path, options);
  }
}

/** Default shared client (base URL ``/api``, Telegram initData auth). */
export const apiClient = new ApiClient();
